"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Client = void 0;
const exposed_promise_1 = require("../../utils/exposed-promise");
const __1 = require("../..");
const events_1 = require("../../events");
const Logger_1 = require("../../utils/Logger");
const is_extension_installed_1 = require("../../utils/is-extension-installed");
const BeaconClient_1 = require("../beacon-client/BeaconClient");
const AccountManager_1 = require("../../managers/AccountManager");
const logger = new Logger_1.Logger('BaseClient');
class Client extends BeaconClient_1.BeaconClient {
    constructor(config) {
        super({ name: config.name, storage: config.storage });
        this.requestCounter = [];
        this.rateLimit = 2;
        this.rateLimitWindowInSeconds = 5;
        this._transport = new exposed_promise_1.ExposedPromise();
        this._isConnected = new exposed_promise_1.ExposedPromise();
        this.events = new events_1.BeaconEventHandler(config.eventHandlers);
        this.accountManager = new AccountManager_1.AccountManager(config.storage);
        this.handleResponse = (message, connectionInfo) => {
            throw new Error(`not overwritten${JSON.stringify(message)} - ${JSON.stringify(connectionInfo)}`);
        };
    }
    get transport() {
        return this._transport.promise;
    }
    get isConnected() {
        return this._isConnected.promise;
    }
    get ready() {
        return this.transport.then(() => undefined);
    }
    async getAccounts() {
        return this.accountManager.getAccounts();
    }
    async getAccount(accountIdentifier) {
        return this.accountManager.getAccount(accountIdentifier);
    }
    async removeAccount(accountIdentifier) {
        return this.accountManager.removeAccount(accountIdentifier);
    }
    async removeAllAccounts() {
        return this.accountManager.removeAllAccounts();
    }
    async addRequestAndCheckIfRateLimited() {
        const now = new Date().getTime();
        this.requestCounter = this.requestCounter.filter((date) => date + this.rateLimitWindowInSeconds * 1000 > now);
        this.requestCounter.push(now);
        return this.requestCounter.length > this.rateLimit;
    }
    async init(isDapp = true, transport) {
        if (this._transport.status === exposed_promise_1.ExposedPromiseStatus.RESOLVED) {
            return (await this.transport).type;
        }
        if (transport) {
            await this.setTransport(transport); // Let users define their own transport
            return transport.type;
        }
        else {
            return new Promise(async (resolve) => {
                const keyPair = await this.keyPair; // We wait for keypair here so the P2P Transport creation is not delayed and causing issues
                const setTransport = async (newTransport) => {
                    await this.setTransport(newTransport);
                    resolve(newTransport.type);
                };
                const setBeaconTransport = async () => {
                    const newTransport = new __1.P2PTransport(this.name, keyPair, this.storage, this.events, isDapp);
                    return setTransport(newTransport);
                };
                const setBeaconTransportTimeout = setTimeout(setBeaconTransport, 200);
                return is_extension_installed_1.isChromeExtensionInstalled.then(async (postMessageAvailable) => {
                    if (postMessageAvailable) {
                        if (setBeaconTransportTimeout) {
                            clearTimeout(setBeaconTransportTimeout);
                        }
                        return setTransport(new __1.PostMessageTransport(this.name));
                    }
                });
            });
        }
    }
    async getPeers() {
        if ((await this.transport).type === __1.TransportType.P2P) {
            return (await this.transport).getPeers();
        }
        else {
            return [];
        }
    }
    async addPeer(id) {
        if ((await this.transport).type === __1.TransportType.P2P) {
            return (await this.transport).addPeer(id);
        }
    }
    async _connect() {
        const transport = await this.transport;
        if (transport.connectionStatus === __1.TransportStatus.NOT_CONNECTED) {
            await transport.connect();
            transport
                .addListener(async (message, connectionInfo) => {
                if (typeof message === 'string') {
                    const deserializedMessage = (await new __1.Serializer().deserialize(message));
                    this.handleResponse(deserializedMessage, connectionInfo);
                }
            })
                .catch((error) => console.log(error));
            this._isConnected.resolve(true);
        }
        else if (transport.connectionStatus === __1.TransportStatus.CONNECTING) {
            await transport.reconnect();
        }
        else {
            // NO-OP
        }
        return this._isConnected.promise;
    }
    async setTransport(transport) {
        if (this._transport.isSettled()) {
            this._transport = new exposed_promise_1.ExposedPromise(); // If the promise has already been resolved we need to create a new one.
        }
        this._transport.resolve(transport);
        this.events.emit(events_1.BeaconEvent.ACTIVE_TRANSPORT_SET, transport).catch((eventError) => {
            logger.error('setTransport', eventError);
        });
    }
}
exports.Client = Client;
//# sourceMappingURL=Client.js.map